// ============================================================
// subscriptionModal.js — Modal elegante de activación de prueba / suscripción
// ============================================================

let activeModalOverlay = null;

/**
 * Muestra el modal de activación de prueba o suscripción requerida.
 * @param {Object} options
 * @param {string} [options.title] - Título del modal
 * @param {string} [options.message] - Mensaje explicativo
 * @param {string} [options.redirectUrl] - URL a la que redirigir si pulsa suscribirse ('precios.html')
 */
export function showSubscriptionModal(options = {}) {
  // Si ya hay un modal abierto, cerrarlo antes
  if (activeModalOverlay) {
    activeModalOverlay.remove();
    activeModalOverlay = null;
  }

  const title = options.title || '¡Activa tu Período de Prueba!';
  const message = options.message || 'Para comenzar a generar imágenes, vídeos y avatares con IA, activa tu prueba gratuita o suscríbete a un plan.';
  const redirectUrl = options.redirectUrl || 'precios.html';

  // Crear el contenedor del modal
  const overlay = document.createElement('div');
  overlay.className = 'vimo-sub-modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  overlay.innerHTML = `
    <div class="vimo-sub-modal-card">
      <button type="button" class="vimo-sub-modal-close" aria-label="Cerrar modal">&times;</button>
      <div class="vimo-sub-modal-header">
        <div class="vimo-sub-modal-icon">
          ✨
        </div>
        <h3 class="vimo-sub-modal-title">${escapeHtml(title)}</h3>
      </div>
      <div class="vimo-sub-modal-body">
        <p>${escapeHtml(message)}</p>
      </div>
      <div class="vimo-sub-modal-actions">
        <button type="button" class="vimo-sub-btn vimo-sub-btn-secondary" id="vimoSubModalCloseBtn">
          Más tarde
        </button>
        <a href="${redirectUrl}" class="vimo-sub-btn vimo-sub-btn-primary" id="vimoSubModalGoBtn">
          Ver Planes y Suscribirme 🚀
        </a>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  activeModalOverlay = overlay;

  // Animación de entrada
  requestAnimationFrame(() => {
    overlay.classList.add('is-visible');
  });

  // Event Listeners
  const closeBtn = overlay.querySelector('.vimo-sub-modal-close');
  const cancelBtn = overlay.querySelector('#vimoSubModalCloseBtn');

  const closeModal = () => {
    overlay.classList.remove('is-visible');
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      if (activeModalOverlay === overlay) activeModalOverlay = null;
    }, 250);
  };

  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);

  // Cerrar haciendo clic fuera del modal
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeModal();
    }
  });

  // Escape key
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
      document.removeEventListener('keydown', handleKeyDown);
    }
  };
  document.addEventListener('keydown', handleKeyDown);
}

/**
 * Muestra el modal de alerta de saldo agotado en proveedores de IA para administradores/demo.
 * @param {Object} options
 */
export function showAdminCreditModal(options = {}) {
  if (activeModalOverlay) {
    activeModalOverlay.remove();
    activeModalOverlay = null;
  }

  const rawMsg = options.message || '';
  const provider = options.provider || (rawMsg.toLowerCase().includes('heygen') ? 'HeyGen' : 'Fal.ai');
  const title = options.title || `⚠️ Límite de Cuota o Créditos IA (${provider})`;

  let friendlyMsg = rawMsg;
  if (/insufficient credit|400133|402|exhausted balance|sin saldo/i.test(rawMsg)) {
    friendlyMsg = `Se han agotado los créditos de API en el proveedor de Inteligencia Artificial (${provider}). Para generar nuevos vídeos con avatar parlante en tiempo real, es necesario recargar saldo o renovar la cuota de API en la cuenta de ${provider}.`;
  }

  const isHeyGen = provider.toLowerCase().includes('heygen');
  const billingUrl = isHeyGen ? 'https://app.heygen.com/settings?nav=credits' : 'https://fal.ai/dashboard/billing';
  const billingLabel = isHeyGen ? 'Panel de HeyGen ↗' : 'Recargar en Fal.ai ↗';

  const overlay = document.createElement('div');
  overlay.className = 'vimo-sub-modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  overlay.innerHTML = `
    <div class="vimo-sub-modal-card" style="border: 2px solid #ef4444; background: #160b0c; max-width: 520px; text-align: left;">
      <button type="button" class="vimo-sub-modal-close" aria-label="Cerrar modal" style="color: #fca5a5;">&times;</button>
      <div class="vimo-sub-modal-header" style="align-items: flex-start; gap: 14px; text-align: left;">
        <div class="vimo-sub-modal-icon" style="background: rgba(239, 68, 68, 0.2); color: #f87171; border: 1px solid rgba(239,68,68,0.4); flex-shrink: 0; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; border-radius: 12px; font-size: 20px;">
          ⚠️
        </div>
        <div>
          <h3 class="vimo-sub-modal-title" style="color: #f87171; font-size: 1.15rem; margin-bottom: 4px;">${escapeHtml(title)}</h3>
          <span style="font-size: 0.75rem; color: #fca5a5; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Aviso de Cuota / Saldo del Proveedor</span>
        </div>
      </div>
      <div class="vimo-sub-modal-body" style="margin-top: 14px; text-align: left;">
        <p style="color: #f3f4f6; font-size: 0.92rem; line-height: 1.6; margin-bottom: 12px;">${escapeHtml(friendlyMsg)}</p>
        <div style="background: rgba(0,0,0,0.5); padding: 12px 14px; border-radius: 8px; font-size: 0.82rem; color: #cbd5e1; border-left: 3px solid #ef4444;">
          <strong>💡 Información técnica:</strong> La aplicación conecta y procesa correctamente con el servidor. Este aviso salta porque la cuenta de API externa (${escapeHtml(provider)}) se ha quedado sin saldo o créditos disponibles.
        </div>
      </div>
      <div class="vimo-sub-modal-actions" style="margin-top: 20px; display: flex; gap: 10px; justify-content: flex-end;">
        <button type="button" class="vimo-sub-btn vimo-sub-btn-primary" id="vimoAdminModalCloseBtn" style="background: rgba(255,255,255,0.14); color: #ffffff; padding: 10px 22px; font-weight: 700; border-radius: 8px; border: 1px solid rgba(255,255,255,0.22); cursor: pointer;">
          Entendido / Aceptar
        </button>
        <a href="${billingUrl}" target="_blank" rel="noopener" class="vimo-sub-btn" style="background: #dc2626; color: #ffffff; text-decoration: none; font-weight: 700; padding: 10px 18px; border-radius: 8px; display: inline-flex; align-items: center;">
          ${billingLabel}
        </a>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  activeModalOverlay = overlay;

  requestAnimationFrame(() => {
    overlay.classList.add('is-visible');
  });

  const closeBtn = overlay.querySelector('.vimo-sub-modal-close');
  const cancelBtn = overlay.querySelector('#vimoAdminModalCloseBtn');

  const closeModal = () => {
    overlay.classList.remove('is-visible');
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      if (activeModalOverlay === overlay) activeModalOverlay = null;
    }, 250);
  };

  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });
}

/**
 * Muestra un modal de confirmación personalizado estilizado con la estética Vimo.
 * Reemplaza el confirm() nativo del navegador.
 * @param {Object} options
 * @param {string} [options.title] - Título del modal
 * @param {string} [options.message] - Texto explicativo
 * @param {string} [options.confirmText] - Texto del botón de confirmación
 * @param {string} [options.cancelText] - Texto del botón de cancelar
 * @returns {Promise<boolean>} Devuelve true si el usuario confirma, false si cancela
 */
export function showConfirmModal(options = {}) {
  return new Promise((resolve) => {
    const title = options.title || '¿Eliminar creación?';
    const message = options.message || 'Esta acción eliminará la creación seleccionada. No se podrá deshacer.';
    const confirmText = options.confirmText || 'Sí, eliminar';
    const cancelText = options.cancelText || 'Cancelar';
    const isDanger = options.type !== 'info';

    const overlay = document.createElement('div');
    overlay.className = 'vimo-sub-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    overlay.innerHTML = `
      <div class="vimo-sub-modal-card" style="border: 1px solid rgba(255,255,255,0.12); background: #161619; max-width: 440px; box-shadow: 0 20px 50px rgba(0,0,0,0.6); padding: 24px;">
        <button type="button" class="vimo-sub-modal-close" aria-label="Cerrar modal">&times;</button>
        <div class="vimo-sub-modal-header" style="align-items: center; gap: 14px; margin-bottom: 8px;">
          <div class="vimo-sub-modal-icon" style="background: ${isDanger ? 'rgba(239, 68, 68, 0.15)' : 'rgba(99, 102, 241, 0.15)'}; color: ${isDanger ? '#f87171' : '#818cf8'}; border: 1px solid ${isDanger ? 'rgba(239, 68, 68, 0.3)' : 'rgba(99, 102, 241, 0.3)'}; font-size: 1.3rem; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; border-radius: 12px; flex-shrink: 0;">
            ${isDanger ? '🗑️' : '❓'}
          </div>
          <h3 class="vimo-sub-modal-title" style="color: #f4f4f5; font-size: 1.15rem; margin: 0; font-weight: 600;">${escapeHtml(title)}</h3>
        </div>
        <div class="vimo-sub-modal-body" style="margin-top: 8px;">
          <p style="color: #a1a1aa; font-size: 0.9rem; line-height: 1.5; margin: 0;">${escapeHtml(message)}</p>
        </div>
        <div class="vimo-sub-modal-actions" style="margin-top: 22px; display: flex; gap: 10px; justify-content: flex-end;">
          <button type="button" class="vimo-sub-btn" id="vimoConfirmCancelBtn" style="background: rgba(255,255,255,0.08); color: #e4e4e7; border: 1px solid rgba(255,255,255,0.1); padding: 9px 18px; border-radius: 8px; font-weight: 500;">
            ${escapeHtml(cancelText)}
          </button>
          <button type="button" class="vimo-sub-btn" id="vimoConfirmOkBtn" style="background: ${isDanger ? '#dc2626' : '#6366f1'}; color: #ffffff; font-weight: 600; padding: 9px 18px; border-radius: 8px;">
            ${escapeHtml(confirmText)}
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
    });

    const closeWithResult = (result) => {
      overlay.classList.remove('is-visible');
      setTimeout(() => {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        resolve(result);
      }, 200);
    };

    overlay.querySelector('.vimo-sub-modal-close')?.addEventListener('click', () => closeWithResult(false));
    overlay.querySelector('#vimoConfirmCancelBtn')?.addEventListener('click', () => closeWithResult(false));
    overlay.querySelector('#vimoConfirmOkBtn')?.addEventListener('click', () => closeWithResult(true));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeWithResult(false);
    });
  });
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

